import React from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { GatsbyImage, StaticImage } from 'gatsby-plugin-image';

import useUserContext from '../hooks/use-user-context';
import { createOptionMap, outputIfNoVariables } from '../utilities/misc';
import { rem } from '../utilities/style';
import { colors } from '../theme/colors';
import { media } from '../theme/media';
import { fontWeights } from '../theme/type';
import { body, headers } from '../styles/type';
import { shadow } from '../styles/box';

import FormattedDate from './FormattedDate';
import TextLink from './TextLink';
import TextRenderer from './TextRenderer';

const Container = styled.div`
  ${shadow}
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background: ${colors.white};
  position: relative;
  width: 100%;

  ${({ displayRow }) => displayRow && `
    ${media('>smedium')} {
      flex-direction: row;
    }
  `};
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: ${rem(10)} ${rem(10)} 0 0;
  overflow: hidden;

  ${media('>small')} {
    ${({ displayRow }) => displayRow && `
    align-self: start;
    max-width: ${rem(450)};
    margin: ${rem(16)};
    border-radius: ${rem(10)};
  `};
  }
`;

const imageStyles = css`
  width: 100%;
`;

const Badge = styled.span`
  ${body.small}
  padding: ${rem(4)} ${rem(12)};
  border-radius: ${rem(100)};
  font-weight: ${fontWeights.semiBold};
  color: ${colors.white};
  background-color: ${colors.blue['800']};
  margin: 0 0 ${rem(15)};
  display: inline-block;

  ${({ overlay }) => overlay && `
    position: absolute;
    top: ${rem(8)};
    left: ${rem(8)};
    margin: 0;
    box-shadow: 0px 4px 16px rgba(29, 37, 45, 0.15);
  `}
`;

const Content = styled.div`
  flex: 1;
  padding: ${rem(12)} ${rem(16)} ${rem(16)};
  display: flex;
  flex-direction: column;
  min-height: ${rem(200)};

  ${({ displayRow }) => displayRow && `
    ${media('>small')} {
      padding: ${rem(30)} ${rem(20)};
    }
    ${media('>medium')} {
      padding: ${rem(50)} ${rem(30)};
    }
  `};

  ${({ featured, displayRow }) => featured && !displayRow && `
    ${media('>small')} {
      padding: ${rem(20)};
    }
    ${media('>medium')} {
      padding: ${rem(30)};
    }
  `};
`;

const TopContent = styled.div`
  flex: 1;
`;

const Category = styled.span`
  display: inline-block;
  position: relative;
  font-size: ${rem(14)};
  line-height: 1.2;
  font-weight: ${fontWeights.semiBold};
  margin-bottom: ${rem(16)};
  color: ${colors.blue['800']};

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: ${rem(3)};
    left: 0;
    bottom: ${rem(-5)};
    background: ${colors.green['500']};
  }
`;

const Title = styled.h3`
  ${headers.h6}
  margin-bottom: ${rem(16)};
  color: ${colors.blue['800']};

  ${({ featured }) => (featured && `
    font-weight: bold;
    font-size: ${rem(24)};

    ${media('>small')} {
      font-size: ${rem(32)};
    }
  `)};
`;

const BottomContent = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${rem(15)};
`;

const ReadingTime = styled.span`
  ${body.small}
  color: ${colors.gray['600']};
`;

const ArticleCardImage = ({ image, imageAlt }) => {
  if (image) {
    return (
      <GatsbyImage
        css={imageStyles}
        image={image}
        alt={imageAlt}
      />
    );
  }

  return (
    <StaticImage
      css={imageStyles}
      src="../images/article-card-placeholder.png"
      alt=""
      role="presentation"
      aria-hidden="true"
      width={640}
      height={427}
      quality="80"
    />
  );
};

const ArticleCard = ({
  displayRow = false,
  category,
  title,
  readingTime,
  link,
  buttonText,
  image,
  imageAlt,
  hideImage = false,
  featured,
  showFeaturedText = true,
  startDate,
  endDate,
  className
}) => {
  const data = useStaticQuery(graphql`
    query ArticleCard {
      allContentfulOptionSet(filter: {name: {eq: "article_card"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const hasDate = startDate && endDate;
  const readingTimeText = readingTime && options['article_card.reading_time']
    ? options['article_card.reading_time'].replace('{{minutes}}', readingTime) : '';

  return (
    <Container className={className} displayRow={displayRow}>
      {!hideImage && (
        <ImageContainer displayRow={displayRow}>
          <ArticleCardImage image={image} imageAlt={imageAlt} />
          {featured && showFeaturedText && !(startDate && endDate) && (
            <Badge overlay>{options['article_card.featured_text']}</Badge>
          )}
        </ImageContainer>
      )}
      <Content displayRow={displayRow} featured={featured}>
        <TopContent>
          {category && (
            <Category>
              <TextRenderer text={category} />
            </Category>
          )}
          <Title featured={featured}>
            <TextRenderer text={title} />
          </Title>
        </TopContent>
        <BottomContent>
          <TextLink
            to={link}
            aria-label={!buttonText ? outputIfNoVariables(`${title} — ${options['article_card.button_text']}`) : null}
            fillContainer
          >
            {hasDate ? (
              <FormattedDate startDate={startDate} endDate={endDate} />
            ) : (
              <TextRenderer text={buttonText || options['article_card.button_text']} />
            )}
          </TextLink>
          {readingTimeText && (
            <ReadingTime>
              {readingTimeText}
            </ReadingTime>
          )}
        </BottomContent>
      </Content>
    </Container>
  );
};

ArticleCard.propTypes = {
  displayRow: PropTypes.bool,
  category: PropTypes.string,
  title: PropTypes.string,
  readingTime: PropTypes.number,
  link: PropTypes.string,
  buttonText: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  image: PropTypes.object,
  imageAlt: PropTypes.string,
  hideImage: PropTypes.bool,
  featured: PropTypes.bool,
  showFeaturedText: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  className: PropTypes.string
};

// Convert to styled component so we can reference it in other component styles
export default styled(ArticleCard)();
